  import React, { useEffect, useMemo, useState } from "react";
  import axios from "axios";
  import api from "./context/api";
  import { useDevice } from "./context/DeviceContext";
  import { useAuth } from "./context/AuthContext";

  const number = (value) => {
    const parsed = Number(String(value ?? "0").replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const compact = (value) => new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);

  export default function ImportExport() {
    const [year, setYear] = useState(new Date().getFullYear());
  const [tradeType, setTradeType] = useState("IMPORT");
  const [viewMode, setViewMode] = useState("PRODUCT");
  const [sortBy, setSortBy] = useState({ key: "value", direction: "desc" });
  const [hideZeroWindow, setHideZeroWindow] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
    const [search, setSearch] = useState("");
    const [expandedProducts, setExpandedProducts] = useState({});
    const [yearDatasets, setYearDatasets] = useState([]);
    const [commodityGroupDatasets, setCommodityGroupDatasets] = useState([]);
    const [commodityGroupError, setCommodityGroupError] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
      let cancelled = false;
      setLoading(true);
      setError("");
      const yearsToLoad = [year - 2, year - 1, year];
      Promise.allSettled(yearsToLoad.map(async (requestedYear) => {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/tradestat/${requestedYear}`);
        return { year: requestedYear, rows: data.rows || [], quarters: data.quarters || [] };
      }))
      .then((results) => {
        if (!cancelled) {
          const datasets = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
          setYearDatasets(datasets);
          if (!datasets.some((dataset) => dataset.year === year && dataset.rows.length)) {
            setError(`No TradeStat data available for ${year}.`);
          }
        }
      })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }, [year]);

    useEffect(() => {
      let cancelled = false;
      setCommodityGroupError("");
      const yearsToLoad = [year - 2, year - 1, year];
      Promise.allSettled(yearsToLoad.map(async (requestedYear) => {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/tradestat/commodity-groups/${requestedYear}`);
        return { year: requestedYear, rows: data.rows || [], quarters: data.quarters || [] };
      }))
        .then((results) => {
          if (cancelled) return;
          const datasets = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
          setCommodityGroupDatasets(datasets);
          if (!datasets.some((dataset) => dataset.year === year && dataset.rows.length)) {
            setCommodityGroupError(`No commodity-group data available for ${year}.`);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setCommodityGroupDatasets([]);
            setCommodityGroupError(
              err.response?.data?.error || "Could not load commodity-group data."
            );
          }
        });
      return () => { cancelled = true; };
    }, [year]);

    const availableQuarters = useMemo(() => {
      const keys = new Set();
      yearDatasets.forEach((dataset) => {
        const suffix = String(dataset.year).slice(-2);
        const quarterNames = dataset.quarters.length ? dataset.quarters
          : Object.keys(dataset.rows[0] || {}).filter((key) => /^Q[1-4]-\d{2}$/.test(key));
        quarterNames.forEach((quarter) => keys.add(/^Q[1-4]-\d{2}$/.test(quarter) ? quarter : `${quarter}-${suffix}`));
      });
      return [...keys].sort((a, b) => {
        const yearDiff = Number(a.slice(-2)) - Number(b.slice(-2));
        return yearDiff || Number(a.slice(1, 2)) - Number(b.slice(1, 2));
      });
    }, [yearDatasets]);
  const visiblePeriods = availableQuarters.slice(-8);

  const tradeBalanceByQuarter = useMemo(() => {
    const totals = new Map(availableQuarters.map((quarter) => [quarter, { imports: 0, exports: 0 }]));
    yearDatasets.forEach((dataset) => {
      const suffix = String(dataset.year).slice(-2);
      const datasetQuarters = dataset.quarters.length ? dataset.quarters
        : Object.keys(dataset.rows[0] || {}).filter((key) => /^Q[1-4]-\d{2}$/.test(key));
      let currentProduct = "";
      dataset.rows.forEach((row) => {
        if (row.Products) currentProduct = row.Products;
        if (!currentProduct || row.Country !== "TOTAL") return;
        const type = String(row.Type || "").toUpperCase();
        if (type !== "IMPORT" && type !== "EXPORT") return;
        datasetQuarters.forEach((quarter) => {
          const key = /^Q[1-4]-\d{2}$/.test(quarter) ? quarter : `${quarter}-${suffix}`;
          if (!totals.has(key)) return;
          totals.get(key)[type === "IMPORT" ? "imports" : "exports"] += number(row[key]);
        });
      });
    });
    return visiblePeriods.map((quarter) => {
      const values = totals.get(quarter) || { imports: 0, exports: 0 };
      return { quarter, ...values, balance: values.exports - values.imports };
    });
  }, [yearDatasets, availableQuarters, visiblePeriods]);
  const maxTradeBalance = Math.max(1, ...tradeBalanceByQuarter.map((item) => Math.abs(item.balance)));

    const products = useMemo(() => {
      const result = new Map();
      yearDatasets.forEach((dataset) => {
        let currentProduct = "";
        const suffix = String(dataset.year).slice(-2);
        const datasetQuarterKeys = dataset.quarters.length ? dataset.quarters
          : Object.keys(dataset.rows[0] || {}).filter((key) => /^Q[1-4]-\d{2}$/.test(key));
        dataset.rows.forEach((row) => {
          if (row.Products) currentProduct = row.Products;
          const product = row.Products || currentProduct;
          if (!product || row.Type !== tradeType) return;
          if (!result.has(product)) result.set(product, { product, values: {}, countryMap: new Map() });
          const productData = result.get(product);
          const values = Object.fromEntries(datasetQuarterKeys.map((q) => {
            const key = /^Q[1-4]-\d{2}$/.test(q) ? q : `${q}-${suffix}`;
            return [key, number(row[key])];
          }));
          if (row.Country === "TOTAL") Object.assign(productData.values, values);
          else if (row.Country) {
            if (!productData.countryMap.has(row.Country)) productData.countryMap.set(row.Country, { country: row.Country, values: {} });
            Object.assign(productData.countryMap.get(row.Country).values, values);
          }
        });
      });
      return [...result.values()].map(({ countryMap, ...product }) => ({
        ...product,
        countries: [...countryMap.values()],
      })).sort((a, b) => availableQuarters.slice(-8).reduce((sum, q) => sum + number(b.values[q]) - number(a.values[q]), 0));
    }, [yearDatasets, tradeType, availableQuarters]);

  const countryGroups = useMemo(() => {
    const groups = new Map();
    products.forEach((product) => {
      product.countries.forEach((countryRow) => {
        if (!groups.has(countryRow.country)) {
          groups.set(countryRow.country, {
            country: countryRow.country,
            values: Object.fromEntries(availableQuarters.map((quarter) => [quarter, 0])),
            commodities: [],
          });
        }
        const group = groups.get(countryRow.country);
        availableQuarters.forEach((quarter) => {
          group.values[quarter] += countryRow.values[quarter] || 0;
        });
        group.commodities.push({
          commodity: product.product,
          values: countryRow.values,
        });
      });
    });
    const groupTotal = (group) => availableQuarters.slice(-8).reduce((sum, quarter) => sum + group.values[quarter], 0);
    return [...groups.values()].sort((a, b) => groupTotal(b) - groupTotal(a));
  }, [products, availableQuarters]);

  const commodityGroups = useMemo(() => {
    const groups = new Map();
    commodityGroupDatasets.forEach((dataset) => {
      const suffix = String(dataset.year).slice(-2);
      dataset.rows.forEach((row) => {
        if (String(row.TYPE).toUpperCase() !== tradeType) return;
        const groupName = String(row.GROUP || "").trim();
        if (!groupName) return;
        const quarter = `${row.QUARTER}-${suffix}`;
        if (!groups.has(groupName)) {
          groups.set(groupName, {
            group: groupName,
            values: Object.fromEntries(availableQuarters.map((q) => [q, null])),
            commodities: new Map(),
          });
        }
        const group = groups.get(groupName);
        const value = number(row.CurrentPeriodValue_USD_Millions);
        if (row["ROW TYPE"] === "GROUP") {
          group.values[quarter] = value;
        } else if (row["ROW TYPE"] === "COMMODITY") {
          const commodityName = String(row.COMMODITY || "").trim();
          if (!group.commodities.has(commodityName)) {
            group.commodities.set(commodityName, {
              commodity: commodityName,
              values: Object.fromEntries(availableQuarters.map((q) => [q, null])),
            });
          }
          group.commodities.get(commodityName).values[quarter] = value;
        }
      });
    });
    return [...groups.values()]
      .map((group) => ({ ...group, commodities: [...group.commodities.values()] }))
      .sort((a, b) => availableQuarters.slice(-8).reduce((sum, q) => sum + number(b.values[q]) - number(a.values[q]), 0));
  }, [commodityGroupDatasets, tradeType, availableQuarters]);

  const activeRows = viewMode === "PRODUCT"
    ? products
    : viewMode === "COUNTRY" ? countryGroups : commodityGroups;
  const displayRows = activeRows.filter((row) => {
    const label = viewMode === "PRODUCT" ? row.product
      : viewMode === "COUNTRY" ? row.country : row.group;
    return label.toLowerCase().includes(search.trim().toLowerCase());
  });

  const chartPeriods = visiblePeriods;
  const groupAvailableQuarters = visiblePeriods.filter((quarter) => commodityGroups.some((row) => row.values[quarter] != null));
  const percentChange = (current, previous) => previous == null || !Number.isFinite(previous) || previous === 0 || current == null
    ? null
    : ((current - previous) / Math.abs(previous)) * 100;
  const formatPercent = (value) => value == null || !Number.isFinite(value)
    ? "—"
    : `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
  const previousQuarterOf = (quarter) => {
    const quarterNumber = Number(quarter.slice(1, 2));
    const quarterYear = Number(quarter.slice(-2));
    return quarterNumber === 1
      ? `Q4-${String(quarterYear - 1).padStart(2, "0")}`
      : `Q${quarterNumber - 1}-${String(quarterYear).padStart(2, "0")}`;
  };
  const yearAgoQuarterOf = (quarter) => `Q${quarter.slice(1, 2)}-${String(Number(quarter.slice(-2)) - 1).padStart(2, "0")}`;
  const quarterChange = (row, quarter) => {
    const previousQuarter = previousQuarterOf(quarter);
    if (!availableQuarters.includes(previousQuarter) || row.values?.[quarter] == null || row.values?.[previousQuarter] == null) return null;
    return percentChange(number(row.values[quarter]), number(row.values[previousQuarter]));
  };
  const currentYearQuarters = availableQuarters.filter((quarter) => Number(`20${quarter.slice(-2)}`) === year);
  const yoyChange = (row) => {
    const comparableQuarters = currentYearQuarters.filter((q) => availableQuarters.includes(yearAgoQuarterOf(q)));
    const priorYearQuarters = comparableQuarters.map(yearAgoQuarterOf);
    const current = comparableQuarters.reduce((sum, q) => sum + number(row.values?.[q]), 0);
    const hasPrevious = priorYearQuarters.some((q) => row.values?.[q] != null);
    const previous = hasPrevious ? priorYearQuarters.reduce((sum, q) => sum + number(row.values?.[q]), 0) : null;
    return percentChange(current, previous);
  };
  const windowTotal = (values) => visiblePeriods.reduce((sum, q) => sum + number(values[q]), 0);
  const sortRows = (items) => {
    const valueFor = (row) => sortBy.key === "yoy" ? yoyChange(row)
      : availableQuarters.includes(sortBy.key) ? number(row.values?.[sortBy.key])
      : windowTotal(row.values);
    return [...items].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * (sortBy.direction === "asc" ? 1 : -1);
    });
  };
  const toggleSort = (key) => setSortBy((current) => ({
    key,
    direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
  }));
  const initialColumnWidth = (key) => key === "label" ? 340
    : key.startsWith("quarter:") ? 105
    : key === "yoy" ? 130
    : 105;
  const widthFor = (key) => columnWidths[key] || initialColumnWidth(key);
  const beginColumnResize = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    setResizingColumn({ key, startX: event.clientX, startWidth: widthFor(key) });
  };
  useEffect(() => {
    if (!resizingColumn) return undefined;
    const handlePointerMove = (event) => {
      const width = Math.max(72, Math.min(720, resizingColumn.startWidth + event.clientX - resizingColumn.startX));
      setColumnWidths((previous) => ({ ...previous, [resizingColumn.key]: width }));
    };
    const handlePointerUp = () => setResizingColumn(null);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [resizingColumn]);
  const resizeHandle = (key) => (
    <span role="separator" aria-orientation="vertical" aria-label="Resize column"
      onPointerDown={(event) => beginColumnResize(event, key)}
      className="absolute right-0 top-0 z-10 h-full w-2 cursor-col-resize touch-none border-r-2 border-slate-300 hover:border-blue-500 hover:bg-blue-300/70" />
  );
  const filteredDisplayRows = hideZeroWindow
    ? displayRows.filter((row) => windowTotal(row.values) !== 0)
    : displayRows;
  const sortedDisplayRows = sortRows(filteredDisplayRows);
  const totals = useMemo(() => chartPeriods.map((q) => ({
      quarter: q,
      available: viewMode !== "GROUP" || groupAvailableQuarters.includes(q),
      value: viewMode === "GROUP"
        ? commodityGroups.reduce((sum, row) => sum + number(row.values[q]), 0)
        : products.reduce((sum, row) => sum + number(row.values[q]), 0),
    })), [products, commodityGroups, chartPeriods, viewMode, groupAvailableQuarters]);
  const grandTotal = totals.reduce((sum, item) => sum + item.value, 0);
  const maxQuarter = Math.max(1, ...totals.map((item) => item.value));

    return (
      <section className="mx-auto max-w-7xl px-3 py-5 sm:px-5">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[.18em] text-blue-600">TradeStat · India</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">Import &amp; Export</h1>
            <p className="mt-1 text-sm font-medium text-slate-500">Figures in US$ millions</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="text-sm font-medium text-slate-600">Through year
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="ml-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900">
                {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div className="mb-5 inline-flex rounded-xl bg-slate-100 p-1" role="tablist" aria-label="Trade direction">
          {["IMPORT", "EXPORT"].map((type) => (
            <button key={type} role="tab" aria-selected={tradeType === type} onClick={() => setTradeType(type)}
              className={`rounded-lg px-5 py-2 text-sm font-semibold transition ${tradeType === type ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              {type === "IMPORT" ? "Imports" : "Exports"}
            </button>
          ))}
        </div>

        {loading ? <div className="rounded-2xl border bg-white p-10 text-center text-slate-500">Loading TradeStat data…</div> : error ?
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</div> : (
          <>
            <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-sm text-slate-500">Total {tradeType === "IMPORT" ? "imports" : "exports"} · last 8 quarters</div>
                <div className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{grandTotal === 0 ? "—" : compact(grandTotal)}</div>
                <div className="mt-1 text-xs text-slate-400">{visiblePeriods[0]} to {visiblePeriods.at(-1)}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-slate-700">Quarterly trend</div>
                <div className="flex h-24 items-end gap-3">
                  {totals.map(({ quarter, value, available }) => (
                    <div key={quarter} className="flex h-full flex-1 flex-col items-center justify-end gap-1" title={available ? `${quarter}: ${value.toLocaleString("en-IN")}` : `${quarter}: report not published`}>
                      <span className="text-[10px] text-slate-500">{available && value !== 0 ? compact(value) : "—"}</span>
                      <div className={`w-full max-w-12 rounded-t-md ${tradeType === "IMPORT" ? "bg-blue-500" : "bg-emerald-500"}`} style={{ height: `${Math.max(value ? 5 : 0, value / maxQuarter * 58)}%` }} />
                      <span className="text-[10px] font-medium text-slate-500">{quarter}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-1 text-sm font-semibold text-slate-700">Trade balance trend</div>
                <div className="mb-2 text-[10px] text-slate-400">Exports − imports · USD millions</div>
                <div className="flex h-24 items-end gap-2">
                  {tradeBalanceByQuarter.map(({ quarter, imports, exports, balance }) => (
                    <div key={quarter} className="flex h-full min-w-0 flex-1 flex-col items-center justify-between"
                      title={`${quarter} · Imports ${compact(imports)} · Exports ${compact(exports)} · ${balance > 0 ? "Surplus" : balance < 0 ? "Deficit" : "Balanced"} ${compact(Math.abs(balance))}`}>
                      <span className={`truncate text-[9px] font-medium ${balance > 0 ? "text-emerald-600" : balance < 0 ? "text-rose-600" : "text-slate-400"}`}>
                        {balance === 0 ? "—" : `${balance > 0 ? "S" : "D"} ${compact(Math.abs(balance))}`}
                      </span>
                      <div className="relative flex h-[54px] w-full items-center justify-center">
                        <div className="absolute inset-x-0 top-1/2 border-t border-slate-200" />
                        {balance !== 0 && <div className={`absolute w-4 rounded-sm ${balance > 0 ? "bottom-1/2 bg-emerald-500" : "top-1/2 bg-rose-500"}`}
                          style={{ height: `${Math.max(2, (Math.abs(balance) / maxTradeBalance) * 25)}px` }} />}
                      </div>
                      <span className="text-[9px] font-medium text-slate-500">{quarter}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-1 flex justify-center gap-4 text-[10px] text-slate-500">
                  <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-500" />Surplus</span>
                  <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-rose-500" />Deficit</span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
                <div>
                  <h2 className="font-semibold text-slate-900">
                    {viewMode === "PRODUCT" ? "Commodity breakdown" : viewMode === "COUNTRY" ? "Country breakdown" : "Commodity group breakdown"}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {filteredDisplayRows.length} {viewMode === "PRODUCT" ? "commodities" : viewMode === "COUNTRY" ? "countries" : "commodity groups"} · last 8 quarters
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <input type="checkbox" checked={hideZeroWindow} onChange={(event) => setHideZeroWindow(event.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                    Hide zero 8-quarter totals
                  </label>
                  <div className="inline-flex rounded-lg bg-slate-100 p-1">
                    {[ ["PRODUCT", "By commodity"], ["COUNTRY", "By country"], ["GROUP", "By commodity groups"] ].map(([mode, label]) => (
                      <button key={mode} type="button" onClick={() => { setViewMode(mode); setExpandedProducts({}); }}
                        className={`rounded-md px-3 py-1.5 text-xs font-semibold ${viewMode === mode ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={viewMode === "PRODUCT" ? "Search commodities…" : viewMode === "COUNTRY" ? "Search countries…" : "Search commodity groups…"} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:w-64" />
                </div>
              </div>
              {viewMode === "GROUP" && commodityGroupError ? (
                <div className="m-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  {commodityGroupError} Make sure the yearly commodity-group CSV exists and the server route is added.
                </div>
              ) : null}
              <div className="overflow-x-auto">
                <table className="min-w-full table-fixed border-separate border-spacing-0 text-sm">
                  <colgroup>
                    <col style={{ width: widthFor("label") }} />
                    {visiblePeriods.map((quarter) => <col key={quarter} style={{ width: widthFor(`quarter:${quarter}`) }} />)}
                    <col style={{ width: widthFor("yoy") }} />
                  </colgroup>
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr>
                    <th className="relative border-b border-r border-slate-300 px-4 py-3 text-left">{viewMode === "PRODUCT" ? "Commodity" : viewMode === "COUNTRY" ? "Country" : "Commodity group"}{resizeHandle("label")}</th>
                    {visiblePeriods.map((q) => <th key={q} className="relative border-b border-r border-slate-300 px-4 py-3 text-right">
                      <button type="button" onClick={() => toggleSort(q)} className="whitespace-nowrap hover:text-blue-700">
                        {q} {sortBy.key === q ? (sortBy.direction === "asc" ? "↑" : "↓") : "↕"}
                      </button>
                      {resizeHandle(`quarter:${q}`)}
                    </th>)}
                    <th className="relative border-b border-r border-slate-300"><button type="button" onClick={() => toggleSort("yoy")} className="whitespace-nowrap px-4 py-3 text-right hover:text-blue-700">YoY % change {sortBy.key === "yoy" ? (sortBy.direction === "asc" ? "↑" : "↓") : "↕"}</button>{resizeHandle("yoy")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedDisplayRows.slice(0, 100).map((row) => {
                      const isProductView = viewMode === "PRODUCT";
                      const isCountryView = viewMode === "COUNTRY";
                      const label = isProductView ? row.product : isCountryView ? row.country : row.group;
                      const details = isProductView ? row.countries : row.commodities;
                      const detailsKey = isProductView ? "country" : "commodity";
                      const isExpanded = Boolean(expandedProducts[label]);

                      const renderValueCells = (values) => visiblePeriods.map((q) => {
                        const unavailable = viewMode === "GROUP" && values[q] == null;
                        const amount = number(values[q]);
                        const showDash = unavailable || amount === 0;
                        const qoq = unavailable ? null : quarterChange({ values }, q);
                        return <td key={q} className="whitespace-nowrap border-r border-slate-200 px-4 py-2 text-right tabular-nums text-slate-700">
                          <div>{showDash ? "—" : compact(amount)}</div>
                          <div className={`text-[10px] font-medium ${qoq > 0 ? "text-emerald-600" : qoq < 0 ? "text-rose-600" : "text-slate-400"}`}>
                            {formatPercent(qoq)}
                          </div>
                        </td>;
                      });

                      return (
                        <React.Fragment key={label}>
                          <tr className="hover:bg-slate-50">
                            <td className="max-w-0 overflow-hidden text-ellipsis whitespace-nowrap border-r border-slate-200 px-4 py-3 font-medium text-slate-800">
                              <button type="button" className="mr-2 text-blue-600" title={isProductView ? "Show partner countries" : "Show details"} aria-expanded={isExpanded}
                                onClick={() => setExpandedProducts((previous) => ({ ...previous, [label]: !previous[label] }))}>
                                {isExpanded ? "−" : "+"}
                              </button>
                              {label}
                              <span className="ml-2 text-xs font-normal text-slate-400">{details.length} {isProductView ? "countries" : "commodities"}</span>
                            </td>
                            {renderValueCells(row.values)}
                            <td className="whitespace-nowrap border-r border-slate-200 px-4 py-3 text-right tabular-nums text-slate-600">{formatPercent(yoyChange(row))}</td>
                          </tr>
                          {isExpanded && details.filter((detail) => !hideZeroWindow || windowTotal(detail.values) !== 0).map((detail) => (
                            <tr key={`${label}-${detail[detailsKey]}`} className="bg-slate-50">
                              <td className="max-w-0 overflow-hidden text-ellipsis whitespace-nowrap border-r border-slate-200 py-2 pl-10 pr-4 text-slate-600">↳ {detail[detailsKey]}</td>
                              {renderValueCells(detail.values)}
                              <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-500">{formatPercent(yoyChange(detail))}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                    {!sortedDisplayRows.length && !(viewMode === "GROUP" && commodityGroupError) && <tr><td colSpan={visiblePeriods.length + 2} className="px-4 py-10 text-center text-slate-500">No matching {viewMode === "PRODUCT" ? "commodities" : viewMode === "COUNTRY" ? "countries" : "commodity groups"}.</td></tr>}
                  </tbody>
                </table>
              </div>
              {sortedDisplayRows.length > 100 && <p className="border-t px-4 py-3 text-xs text-slate-500">Showing top 100 rows. Search to find others.</p>}
            </div>
          </>
        )}
        <p className="mt-3 text-xs text-slate-400">All figures are in USD millions (USD mn).</p>
      </section>
    );
  }
